import React, { useState } from 'react';
import { X, TrendingUp } from 'lucide-react';

const KPIModal = ({ show, onClose, onSubmit, engagementId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetValue: '',
    actualValue: '',
    unit: '',
    category: 'output'
  });

  const kpiCategories = [
    { value: 'output', label: 'Output Indicator' },
    { value: 'outcome', label: 'Outcome Indicator' },
    { value: 'impact', label: 'Impact Indicator' },
    { value: 'process', label: 'Process Indicator' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const kpiData = {
      engagementId,
      name: formData.name,
      description: formData.description,
      targetValue: parseFloat(formData.targetValue),
      actualValue: parseFloat(formData.actualValue) || 0,
      unit: formData.unit,
      category: formData.category
    };

    await onSubmit(kpiData);
    
    // Reset form
    setFormData({
      name: '',
      description: '',
      targetValue: '',
      actualValue: '',
      unit: '',
      category: 'output'
    });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-muted-foreground" />
            <h2 className="heading-3 text-foreground">
              Add Key Performance Indicator
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="label mb-2 block">
              KPI Category *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="input-base"
            >
              {kpiCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label mb-2 block">
              KPI Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-base"
              placeholder="e.g., Number of Beneficiaries Trained"
            />
          </div>

          <div>
            <label className="label mb-2 block">
              Description
            </label>
            <textarea
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-base resize-none"
              placeholder="Describe how this indicator is measured..."
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="label mb-2 block">
                Target Value *
              </label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.targetValue}
                onChange={(e) => setFormData({...formData, targetValue: e.target.value})}
                className="input-base"
                placeholder="e.g., 500"
              />
            </div>

            <div>
              <label className="label mb-2 block">
                Current/Actual Value
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.actualValue}
                onChange={(e) => setFormData({...formData, actualValue: e.target.value})}
                className="input-base"
                placeholder="e.g., 250"
              />
            </div>
          </div>

          <div>
            <label className="label mb-2 block">
              Unit of Measurement *
            </label>
            <input
              type="text"
              required
              value={formData.unit}
              onChange={(e) => setFormData({...formData, unit: e.target.value})}
              className="input-base"
              placeholder="e.g., People, Hours, Percentage, etc."
            />
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="text-sm text-foreground">
              <strong>KPI Categories Explained:</strong>
              <ul className="mt-2 space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong className="text-foreground">Output:</strong> Direct products/services delivered (e.g., training sessions conducted)</li>
                <li><strong className="text-foreground">Outcome:</strong> Short-term changes (e.g., skills gained by trainees)</li>
                <li><strong className="text-foreground">Impact:</strong> Long-term changes (e.g., employment rate increase)</li>
                <li><strong className="text-foreground">Process:</strong> Efficiency metrics (e.g., time to complete activities)</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline flex-1 btn-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 btn-lg"
            >
              Add KPI
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KPIModal;
